// TS mirrors of the .NET domain models exposed over /api.
// ASP.NET Core serializes with camelCase by default; field names below match
// the JSON wire shape, not the C# PascalCase property names.

import type { PropertyHeader } from "./property/types";

export interface ParsedExtraction extends PropertyHeader {
  objections: ObjectionEntry[];

  debugCondensedContext?: string | null;
  debugObjectionsSource?: string | null;
  debugInfo?: DebugExtractionInfo | null;
}

export type ObjectionAssignee = "Applicant" | "Owner" | "Speedy" | "EN1";
export const ASSIGNEE_OPTIONS: ObjectionAssignee[] = [
  "Applicant",
  "Owner",
  "Speedy",
];

export interface ObjectionEntry {
  id: string; // Guid
  text: string; // legacy alias
  objectionText: string;
  resolved: boolean;
  response?: string | null;
  sourceFileName?: string | null;
  sourceFileId?: string | null;
  /** Required at PDF-generation time; the radio in Review fills this in. */
  assignedTo?: ObjectionAssignee | null;
  pageNumber?: number | null;
  annotationType?: string | null;
  rawExtractedLine?: string | null;
  createdAt?: string | null;
}

export interface ExtractionResult {
  rawText: string;
  annotations: ExtractedAnnotation[];
  metadata: ExtractionMetadata;
  fileName: string;
  tableRegionRows?: ExtractedTableRow[] | null;
}

export interface ExtractedAnnotation {
  pageNumber: number;
  content: string;
  isResolved: boolean;
  response?: string | null;
  type: string;
  timestamp?: string | null;
  author?: string | null;
}

export interface ExtractionMetadata {
  isExcelData: boolean;
  totalPages: number;
  totalAnnotations: number;
  processingNotes: string[];
}

export interface ExtractedTableRow {
  pageNumber: number;
  rowIndex: number;
  cells: string[];
  fullRowText: string;
  hasContent: boolean;
}

export interface DebugExtractionInfo {
  rawAnnotations: DebugAnnotation[];
  filteredAnnotations: string[];
  dobMarkupObjections: DebugTableObjection[];
  standardTableObjections: DebugTableObjection[];
  annotationBlockObjections: DebugTableObjection[];
  rawTableRegionRows: DebugTableRegionRow[];
  finalMergedObjections: string[];
  extractionTimestamp: string;
  processingNotes: string[];
}

export interface DebugAnnotation {
  pageNumber: number;
  annotationType: string;
  extractedContent?: string | null;
  toStringOutput?: string | null;
  wasFiltered: boolean;
  filterReason?: string | null;
}

export interface DebugTableObjection {
  source: string;
  objectionText: string;
  resolved: boolean;
  rawExtractedLine?: string | null;
}

export interface DebugTableRegionRow {
  pageNumber: number;
  rowIndex: number;
  cells: string[];
}

// =============================================================================
// PDF Region Selector
// =============================================================================

export interface PdfTableRegion {
  // Percentages 0-100 — scale-independent.
  x: number;
  y: number;
  width: number;
  height: number;
  createdAt?: string;
}

export interface PdfTableRegionCollection {
  // page number (1-indexed) -> region
  regions: Record<number, PdfTableRegion>;
  totalPages: number;
}

export interface PdfPageRenderResult {
  pageNumber: number;
  totalPages: number;
  base64Image: string;
  originalWidth: number;
  originalHeight: number;
  success: boolean;
  errorMessage?: string | null;
}

// Local UI state for a file as it moves through the workflow.
export type FileStatus =
  | "Pending"
  | "Processing"
  | "Completed"
  | "Failed";

// EN files trigger the EN1 assignee pill in Review. Auto-detected from the
// filename (uppercase, standalone "EN") and overridable per file.
export type FileKind = "Regular" | "EN";

export interface FileUploadItem {
  // Local UUID — not server-issued. Used for React keys + per-file dispatching.
  id: string;
  file: File;
  status: FileStatus;
  kind: FileKind;
  errorMessage?: string;
  extraction?: ExtractionResult;
  parsed?: ParsedExtraction;
  // Per-file region collection (PDFs only, set via PdfRegionSelectorDialog).
  tableRegions?: PdfTableRegionCollection;
}

// Standalone uppercase "EN" anywhere in the filename → EN. Case-sensitive,
// word-boundary (so "Energy", "BEN", "en", "EN1" all stay Regular).
export function detectFileKind(filename: string): FileKind {
  return /\bEN\b/.test(filename) ? "EN" : "Regular";
}

// Determines the session-type label sent to the stats API. Matches the new
// rule (fix per clarification #3): PDF, Excel, CSV, BAX as appropriate.
export function fileTypeLabel(filename: string): "PDF" | "Excel" | "CSV" | "BAX" {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".pdf")) return "PDF";
  if (lower.endsWith(".csv")) return "CSV";
  if (lower.endsWith(".bax")) return "BAX";
  return "Excel";
}

export function isPdfFile(filename: string): boolean {
  return filename.toLowerCase().endsWith(".pdf");
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
